import classNames from 'classnames';
import type { ReactElement, Ref } from 'react';
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import type { ImageModalData, ImageModalProps, ImageModalRef } from './ImageModal.data';
import styles from './ImageModal.module.scss';
import Image from '../../atoms/image/Image';
import Modal from '../../molecules/modal/Modal';

function ImageModal({
  className,
  ref,
}: ImageModalProps & { ref?: Ref<ImageModalRef> }): ReactElement {
  const [src, setSrc] = useState<string>();
  const [title, setTitle] = useState<string>();
  const onCloseCallback = useRef<() => void>(undefined);
  const modalRef = useRef<HTMLDivElement>(null);

  const open = useCallback((data: ImageModalData) => {
    if (typeof data?.image === 'string') {
      setSrc(data.image);
      setTitle(data.title);
    } else if (data) {
      setSrc(data.image.filename);
      setTitle(data.title);
    }

    onCloseCallback.current = data?.options?.onClose;
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      open,
    }),
    [open],
  );

  const handleClose = useCallback(() => {
    if (onCloseCallback.current) {
      onCloseCallback.current();
    }
    setSrc(undefined);
  }, []);

  return (
    <Modal
      open={!!src}
      className={classNames(styles.imageModal, className)}
      onClose={handleClose}
      title={title}
    >
      <div className={styles.modalContainer}>
        <div ref={modalRef} className={classNames(styles.modal)}>
          {src && (
            <Image
              src={src}
              srcDesktop={src}
              layout="fill"
              objectFit="contain"
              className={classNames(styles.image)}
              alt="image"
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

ImageModal.displayName = 'ImageModal';
export default ImageModal;
